// client/src/components/volunteers/VolunteersPage.jsx
//
// /:tenant/volunteers (Roadmap Phase 5.2):
//   - Shifts & check-in: pick an event, add shifts (role, time, people
//     needed), share the sign-up form, and on the day check people in and out
//     or log their hours. Totals for the event at the top.
//   - Volunteers: hours per person, all time or one year.
//
// API: /api/volunteers/:tenant (server/routes/volunteerRoutes.js). Owners,
// admins and managers edit shifts; operators can check people in.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaWpforms } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import { getJson } from '../../services/http';
import {
  listVolunteerEvents, getRoster, createShift, updateShift, deleteShift, addSignup, updateSignup, cancelSignup,
  listSignupForms, createSignupForm, listVolunteerPeople, formatHours, errorText,
} from '../../services/volunteersApi';
import '../publishing/Publishing.css';
import '../donations/Donations.css';

const EMPTY_SHIFT = { role: '', date: '', startTime: '09:00', endTime: '12:00', slots: '', location: '', description: '' };

function ShiftForm({ initial, onSave, onCancel, t }) {
  const [f, setF] = useState(() => (initial ? {
    role: initial.role, date: initial.date, startTime: initial.startTime, endTime: initial.endTime,
    slots: initial.slots === null ? '' : String(initial.slots), location: initial.location, description: initial.description,
  } : EMPTY_SHIFT));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await onSave({ ...f, slots: f.slots === '' ? null : Number(f.slots) });
    } catch (err) {
      setError(errorText(err, t('volunteers.shift.saveError', 'The shift could not be saved.')));
      setBusy(false);
    }
  };
  return (
    <form className="pb-form" onSubmit={submit} data-testid="shift-form">
      <label className="pb-field" htmlFor="vs-role">
        {t('volunteers.shift.role', 'Role')}
        <input id="vs-role" value={f.role} onChange={(e) => set('role', e.target.value)} placeholder={t('volunteers.shift.rolePlaceholder', 'Ticket table')} required maxLength={120} />
      </label>
      <label className="pb-field" htmlFor="vs-date">
        {t('volunteers.shift.date', 'Date')}
        <input id="vs-date" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} required />
      </label>
      <label className="pb-field" htmlFor="vs-start">
        {t('volunteers.shift.start', 'Starts')}
        <input id="vs-start" type="time" value={f.startTime} onChange={(e) => set('startTime', e.target.value)} required />
      </label>
      <label className="pb-field" htmlFor="vs-end">
        {t('volunteers.shift.end', 'Ends')}
        <input id="vs-end" type="time" value={f.endTime} onChange={(e) => set('endTime', e.target.value)} required />
      </label>
      <label className="pb-field" htmlFor="vs-slots">
        {t('volunteers.shift.slots', 'People needed (blank = no limit)')}
        <input id="vs-slots" type="number" min="1" value={f.slots} onChange={(e) => set('slots', e.target.value)} />
      </label>
      <label className="pb-field" htmlFor="vs-location">
        {t('volunteers.shift.location', 'Where')}
        <input id="vs-location" value={f.location} onChange={(e) => set('location', e.target.value)} maxLength={200} />
      </label>
      <label className="pb-field pb-wide" htmlFor="vs-desc">
        {t('volunteers.shift.description', 'What they’ll do')}
        <textarea id="vs-desc" rows={2} value={f.description} onChange={(e) => set('description', e.target.value)} maxLength={1000} />
      </label>
      {error && <div className="note error pb-wide" role="alert">{error}</div>}
      <div className="pb-actions pb-wide">
        <button type="submit" className="pb-btn primary" disabled={busy}>{t('volunteers.shift.save', 'Save shift')}</button>
        <button type="button" className="pb-btn" onClick={onCancel}>{t('common.cancel', 'Cancel')}</button>
      </div>
    </form>
  );
}

function AddPerson({ onAdd, t }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [open, setOpen] = useState(false);
  if (!open) return <button type="button" className="pb-btn" onClick={() => setOpen(true)}><FaPlus aria-hidden="true" /> {t('volunteers.roster.add', 'Add a volunteer')}</button>;
  return (
    <form className="pb-actions" onSubmit={async (e) => { e.preventDefault(); if (await onAdd({ name, email })) { setName(''); setEmail(''); setOpen(false); } }}>
      <div className="pb-field">
        <input aria-label={t('volunteers.roster.name', 'Name')} placeholder={t('volunteers.roster.name', 'Name')} value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="pb-field">
        <input aria-label={t('volunteers.roster.email', 'Email')} placeholder={t('volunteers.roster.email', 'Email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      <button type="submit" className="pb-btn primary">{t('volunteers.roster.addButton', 'Add')}</button>
      <button type="button" className="pb-btn" onClick={() => setOpen(false)}>{t('common.cancel', 'Cancel')}</button>
    </form>
  );
}

export default function VolunteersPage({ tenantId }) {
  const params = useParams();
  const tenant = tenantId || params.tenant;
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const { t } = useTranslation();
  const [tab, setTab] = useState('shifts');
  const [ready, setReady] = useState(null);
  const [denied, setDenied] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(search.get('event') || '');
  const [roster, setRoster] = useState(null);
  const [forms, setForms] = useState(null);
  const [editing, setEditing] = useState(null);
  const [people, setPeople] = useState(null);
  const [peopleDenied, setPeopleDenied] = useState(false);
  const [year, setYear] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([
      listVolunteerEvents(tenant),
      getJson(`/api/events/${tenant}/events`).catch(() => ({})),
    ]).then(([v, all]) => {
      if (!alive) return;
      setReady(v.ready !== false);
      const list = (all.data || all.events || []).map((e) => ({ id: Number(e.id), name: e.name, startdate: e.startdate }));
      setEvents(list);
      if (!eventId) {
        const first = (v.events || [])[0];
        const pick = first ? first.eventId : (list[0] && list[0].id);
        if (pick) setEventId(String(pick));
      }
    }).catch((err) => {
      if (!alive) return;
      if (err?.response?.status === 403) setDenied(true);
      else setError(errorText(err, t('volunteers.loadError', 'Volunteers could not be loaded.')));
    });
    return () => { alive = false; };
  }, [tenant]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRoster = useCallback(async () => {
    if (!eventId) return;
    try {
      setRoster(await getRoster(tenant, eventId));
    } catch (err) { setError(errorText(err, t('volunteers.loadError', 'Volunteers could not be loaded.'))); }
    listSignupForms(tenant, eventId).then((r) => setForms(r.ready === false ? null : r.forms)).catch(() => setForms(null));
  }, [tenant, eventId, t]);

  useEffect(() => {
    if (ready && tab === 'shifts' && eventId) { setRoster(null); loadRoster(); }
  }, [ready, tab, eventId, loadRoster]);

  useEffect(() => {
    if (!ready || tab !== 'people') return;
    setPeople(null);
    listVolunteerPeople(tenant, year || null).then((r) => setPeople(r.people || [])).catch((err) => {
      if (err?.response?.status === 403) setPeopleDenied(true);
      else setError(errorText(err, t('volunteers.loadError', 'Volunteers could not be loaded.')));
    });
  }, [ready, tab, tenant, year, t]);

  const flash = (m) => { setNotice(m); setError(''); };
  const pickEvent = (id) => { setEventId(id); setEditing(null); setSearch(id ? { event: id } : {}); };

  const saveShift = async (body) => {
    if (editing && editing !== 'new') await updateShift(tenant, editing.id, body);
    else await createShift(tenant, eventId, body);
    setEditing(null);
    flash(t('volunteers.shift.saved', 'Shift saved.'));
    await loadRoster();
  };
  const removeShift = async (s) => {
    if (!window.confirm(t('volunteers.shift.confirmDelete', 'Delete the “{{role}}” shift? People signed up for it won’t be reminded.', { role: s.role }))) return;
    try {
      const r = await deleteShift(tenant, s.id);
      flash(r.archived ? t('volunteers.shift.archived', 'Hours were logged on this shift, so it was turned off instead of deleted.') : t('volunteers.shift.deleted', 'Shift deleted.'));
      await loadRoster();
    } catch (err) { setError(errorText(err, t('volunteers.shift.saveError', 'The shift could not be saved.'))); }
  };
  const act = async (signup, body) => {
    try {
      await updateSignup(tenant, signup.id, body);
      await loadRoster();
    } catch (err) { setError(errorText(err, t('volunteers.roster.actionError', 'That didn’t work. Try again.'))); }
  };
  const logHours = (signup) => {
    const input = window.prompt(t('volunteers.roster.hoursPrompt', 'Hours {{name}} worked:', { name: signup.name }), signup.hours ? String(signup.hours) : '');
    if (input === null) return;
    act(signup, { action: 'log_hours', hours: Number(input) });
  };
  const cancel = async (signup) => {
    if (!window.confirm(t('volunteers.roster.confirmCancel', 'Take {{name}} off this shift?', { name: signup.name }))) return;
    try { await cancelSignup(tenant, signup.id); await loadRoster(); } catch (err) { setError(errorText(err, t('volunteers.roster.actionError', 'That didn’t work. Try again.'))); }
  };
  const add = async (shiftId, body) => {
    try {
      await addSignup(tenant, shiftId, body);
      await loadRoster();
      return true;
    } catch (err) { setError(errorText(err, t('volunteers.roster.actionError', 'That didn’t work. Try again.'))); return false; }
  };
  const makeForm = async () => {
    try {
      await createSignupForm(tenant, eventId);
      flash(t('volunteers.form.created', 'Sign-up form created. Share its link, or find it on the event’s public page.'));
      const r = await listSignupForms(tenant, eventId);
      setForms(r.forms);
    } catch (err) { setError(errorText(err, t('volunteers.form.createError', 'The sign-up form could not be created.'))); }
  };

  const status = {
    signed_up: t('volunteers.status.signedUp', 'Signed up'), checked_in: t('volunteers.status.checkedIn', 'Checked in'),
    completed: t('volunteers.status.completed', 'Done'), no_show: t('volunteers.status.noShow', 'No-show'), canceled: t('volunteers.status.canceled', 'Canceled'),
  };
  const buttons = [
    { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
    { icon: <FaWpforms />, label: t('volunteers.forms', 'Forms'), onClick: () => navigate(`/${tenant}/forms`) },
  ];

  return (
    <div className="pb-page">
      <DesktopActionBar buttons={buttons} />
      <section className="surface">
        <h2>{t('volunteers.title', 'Volunteers')}</h2>
        <p className="surface-muted">
          {t('volunteers.intro', 'Add shifts to an event, share the sign-up form, and On-Screen reminds volunteers by email (and push, if they turned it on) the day before and an hour before. On the day, check people in and out; their hours add up per person and per event.')}
        </p>
      </section>

      {ready === null && !error && !denied && <LoadingState />}
      {denied && <div className="note info" role="status">{t('volunteers.denied', 'Owners, admins, managers and operators manage volunteers.')}</div>}
      {error && <div className="note error" role="alert">{error}</div>}
      {notice && <div className="note ok" role="status">{notice}</div>}
      {ready === false && <div className="note warn" role="status">{t('volunteers.migration', 'Volunteers need a database update before they can be used.')}</div>}

      {ready && (
        <>
          <div className="pb-tabs" role="tablist" aria-label={t('volunteers.title', 'Volunteers')}>
            <button type="button" role="tab" className="pb-tab" aria-selected={tab === 'shifts'} onClick={() => setTab('shifts')}>{t('volunteers.tab.shifts', 'Shifts & check-in')}</button>
            <button type="button" role="tab" className="pb-tab" aria-selected={tab === 'people'} onClick={() => setTab('people')}>{t('volunteers.tab.people', 'Volunteers')}</button>
          </div>

          {tab === 'shifts' && (
            <>
              <section className="surface">
                <label className="pb-field" htmlFor="vp-event">
                  {t('volunteers.event', 'Event')}
                  <select id="vp-event" value={eventId} onChange={(e) => pickEvent(e.target.value)}>
                    <option value="">{t('volunteers.pickEvent', 'Pick an event')}</option>
                    {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </label>
                {!eventId && <p className="pb-ai-meta">{t('volunteers.noEvent', 'Pick an event to add shifts.')}</p>}
                {roster && (
                  <p className="pb-summary" data-testid="event-totals">
                    {t('volunteers.totals', '{{volunteers}} volunteers · {{signups}} sign-ups · {{completed}} shifts done · {{hours}} logged', {
                      volunteers: roster.totals.volunteers, signups: roster.totals.signups, completed: roster.totals.completed, hours: formatHours(roster.totals.hours),
                    })}
                  </p>
                )}
              </section>

              {eventId && forms && (
                <section className="surface" data-testid="signup-form">
                  <h3 style={{ marginTop: 0 }}>{t('volunteers.form.title', 'Sign-up form')}</h3>
                  {forms.length === 0 ? (
                    <>
                      <p className="pb-ai-meta">{t('volunteers.form.none', 'Volunteers sign up through a form that lists this event’s open shifts. It also shows on the event’s public page.')}</p>
                      <button type="button" className="pb-btn primary" onClick={makeForm}><FaWpforms aria-hidden="true" /> {t('volunteers.form.create', 'Create the sign-up form')}</button>
                    </>
                  ) : forms.map((f) => (
                    <div key={f.id} className="dn-copy">
                      <input readOnly value={f.url || ''} aria-label={t('volunteers.form.link', 'Sign-up form link')} onFocus={(e) => e.target.select()} />
                      <span className="pb-ai-meta">{t('volunteers.form.submissions', '{{count}} sign-ups', { count: f.submissions })}</span>
                      <button type="button" className="pb-btn" onClick={() => navigate(`/${tenant}/forms/${f.id}`)}>{t('volunteers.form.edit', 'Edit form')}</button>
                    </div>
                  ))}
                </section>
              )}

              {eventId && roster && (
                <section className="surface" data-testid="shifts">
                  <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
                    <h3 style={{ margin: 0 }}>{t('volunteers.shifts', 'Shifts')}</h3>
                    {editing === null && <button type="button" className="pb-btn primary" onClick={() => setEditing('new')}><FaPlus aria-hidden="true" /> {t('volunteers.shift.new', 'Add a shift')}</button>}
                  </div>
                  {editing !== null && <ShiftForm initial={editing === 'new' ? null : editing} onSave={saveShift} onCancel={() => setEditing(null)} t={t} />}
                  {editing === null && roster.shifts.length === 0 && <p className="pb-ai-meta">{t('volunteers.shift.empty', 'No shifts yet. Add one for each job and time slot, for example “Ticket table, 9 AM to noon, 3 people”.')}</p>}
                  {editing === null && roster.shifts.map((s) => (
                    <article key={s.id} className="pb-feed" data-testid="shift">
                      <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
                        <p className="pb-feed-title" style={{ margin: 0 }}>{s.role}{!s.active && <> <span className="pb-badge muted">{t('volunteers.shift.off', 'Off')}</span></>}</p>
                        <span className={`pb-badge ${s.full ? 'warn' : 'ok'}`}>
                          {s.slots === null
                            ? t('volunteers.shift.countOpen', '{{taken}} signed up', { taken: s.taken })
                            : t('volunteers.shift.count', '{{taken}} of {{slots}}', { taken: s.taken, slots: s.slots })}
                        </span>
                      </div>
                      <p className="pb-feed-meta" style={{ margin: '4px 0' }}>{s.when}{s.location && ` · ${s.location}`}</p>
                      {s.signups.length > 0 && (
                        <div className="pb-table-wrap">
                          <table className="pb-table">
                            <thead><tr>
                              <th>{t('volunteers.roster.name', 'Name')}</th><th>{t('volunteers.roster.status', 'Status')}</th>
                              <th>{t('volunteers.roster.hours', 'Hours')}</th><th>{t('volunteers.roster.actions', 'Actions')}</th>
                            </tr></thead>
                            <tbody>
                              {s.signups.map((p) => (
                                <tr key={p.id} data-testid="signup">
                                  <td>{p.name}<br /><span className="pb-ai-meta">{p.email}</span></td>
                                  <td>{status[p.status] || p.status}</td>
                                  <td>{p.hours !== null ? formatHours(p.hours) : '—'}</td>
                                  <td>
                                    <div className="pb-actions">
                                      {p.status === 'signed_up' && <button type="button" className="pb-btn primary" onClick={() => act(p, { action: 'check_in' })}>{t('volunteers.roster.checkIn', 'Check in')}</button>}
                                      {p.status === 'checked_in' && <button type="button" className="pb-btn primary" onClick={() => act(p, { action: 'check_out' })}>{t('volunteers.roster.checkOut', 'Check out')}</button>}
                                      {['signed_up', 'checked_in', 'completed', 'no_show'].includes(p.status) && <button type="button" className="pb-btn" onClick={() => logHours(p)}>{t('volunteers.roster.logHours', 'Log hours')}</button>}
                                      {p.status === 'signed_up' && <button type="button" className="pb-btn" onClick={() => act(p, { action: 'no_show' })}>{t('volunteers.roster.noShow', 'No-show')}</button>}
                                      {p.status === 'signed_up' && <button type="button" className="pb-btn danger" onClick={() => cancel(p)}>{t('volunteers.roster.remove', 'Remove')}</button>}
                                      {['completed', 'no_show', 'canceled'].includes(p.status) && <button type="button" className="pb-btn" onClick={() => act(p, { action: 'reset' })}>{t('volunteers.roster.reset', 'Undo')}</button>}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <div className="pb-actions">
                        {s.active && !s.full && <AddPerson onAdd={(body) => add(s.id, body)} t={t} />}
                        <button type="button" className="pb-btn" onClick={() => setEditing(s)}><FaEdit aria-hidden="true" /> {t('volunteers.edit', 'Edit')}</button>
                        <button type="button" className="pb-btn danger" onClick={() => removeShift(s)}><FaTrash aria-hidden="true" /> {t('volunteers.delete', 'Delete')}</button>
                      </div>
                    </article>
                  ))}
                </section>
              )}
            </>
          )}

          {tab === 'people' && (
            <section className="surface" data-testid="people">
              <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0 }}>{t('volunteers.people.title', 'Hours per volunteer')}</h3>
                <label className="pb-field" htmlFor="vp-year">
                  {t('volunteers.people.period', 'Period')}
                  <select id="vp-year" value={year} onChange={(e) => setYear(e.target.value)}>
                    <option value="">{t('volunteers.people.allTime', 'All time')}</option>
                    {[0, 1, 2].map((n) => { const y = new Date().getFullYear() - n; return <option key={y} value={y}>{y}</option>; })}
                  </select>
                </label>
              </div>
              {peopleDenied && <p className="pb-ai-meta">{t('volunteers.people.denied', 'Owners, admins and managers see hours per volunteer.')}</p>}
              {!peopleDenied && !people && <LoadingState />}
              {people && people.length === 0 && <p className="pb-ai-meta">{t('volunteers.people.empty', 'No volunteers yet.')}</p>}
              {people && people.length > 0 && (
                <div className="pb-table-wrap">
                  <table className="pb-table">
                    <thead><tr>
                      <th>{t('volunteers.roster.name', 'Name')}</th><th>{t('volunteers.people.hours', 'Hours')}</th><th>{t('volunteers.people.shifts', 'Shifts worked')}</th>
                      <th>{t('volunteers.people.upcoming', 'Upcoming')}</th><th>{t('volunteers.people.noShows', 'No-shows')}</th><th>{t('volunteers.people.events', 'Events')}</th>
                    </tr></thead>
                    <tbody>
                      {people.map((p) => (
                        <tr key={p.email}>
                          <td>{p.name}<br /><span className="pb-ai-meta">{p.email}</span></td>
                          <td>{formatHours(p.hours)}</td><td>{p.shiftsWorked}</td><td>{p.upcoming}</td><td>{p.noShows}</td><td>{p.events}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}
      <AppFooter buttons={buttons} />
    </div>
  );
}
